import { Component, OnInit, OnDestroy, EventEmitter } from "@angular/core";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { BaseComponent } from "../../../../pages/shared/base.component";
import { UnsubscriberService } from "../../../../services/unsubscribe.service";
import { filter } from "rxjs/operators";
import { ProductService } from "../product.service";
import { NotificationAlertService } from "src/app/services/notification.service";
import {
  CompletionStageEnum,
  Product,
  ProductStatus,
  ProductType,
} from "../tour.models";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject } from "rxjs";
import { P, match } from "ts-pattern";
import { ProductEditConfirmationService } from "./product-edit-confirmation.service";

export type StepGroup = {
  title: string;
  steps: Step[];
};

export type Step = {
  id: string;
  label: string;
  route: string;
  completed: boolean;
};

const productCreationSteps = {
  basicDetailsGroup: {
    title: "A. Basic Detail Steps",
    steps: [
      {
        id: "basic-details",
        label: "Basic Details",
        route: "basic-details",
        completed: false,
      },
      // {
      //   id: "product-type",
      //   label: "2. Product Type",
      //   route: "product-type",
      //   completed: false,
      // },
      // {
      //   id: "categories",
      //   label: "3. Themes",
      //   route: "categories",
      //   completed: false,
      // },
      {
        id: "media",
        label: "Add Photos",
        route: "media",
        completed: false,
      },
      {
        id: "themes-categories",
        label: "Themes & Categories",
        route: "themes-categories",
        completed: false,
      },
    ],
  },
  productContentGroup: {
    title: "B. Product Content Steps",
    steps: [
      {
        id: "meeting-pickup",
        label: "Meeting and Pickup",
        route: "meeting-pickup",
        completed: false,
      },
      {
        id: "tour-activity-details",
        label: "Tour/Activity Details",
        route: "tour-activity-details",
        completed: false,
      },
      {
        id: "addons",
        label: "Addons",
        route: "addons",
        completed: false,
      },
      {
        id: "special",
        label: "What makes your tour special",
        route: "special",
        completed: false,
      },
      {
        id: "additional-info",
        label: "Additional Information for traveller",
        route: "additional-info",
        completed: false,
      },
    ],
  },
  schedulesPricingGroup: {
    title: "C. Schedules and Pricing",
    steps: [
      {
        id: "pricing-setup",
        label: "Traveler details",
        route: "pricing-setup",
        completed: false,
      },
      {
        id: "scheduling",
        label: "Scheduling",
        route: "scheduling",
        completed: false,
      },
    ],
  },
  bookingsTicketsGroup: {
    title: "D. Bookings & Tickets",
    steps: [
      {
        id: "booking-settings",
        label: "Booking Settings",
        route: "booking-settings",
        completed: false,
      },
      {
        id: "cancellation-policy",
        label: "Cancellation Policy",
        route: "cancellation-policy",
        completed: false,
      },
    ],
  },
  finish: {
    title: "E. Finish",
    steps: [
      {
        id: "submit-for-review",
        label: "Publish",
        route: "submit-for-review",
        completed: false,
      },
      {
        id: "product-status",
        label: "Product Status",
        route: "product-status",
        completed: false,
      },
    ],
  },
};

export const allStepIdsOrdered = Object.values(productCreationSteps).flatMap(
  (pC) => pC.steps.map((step) => step.id),
);

@Component({
  selector: "app-product-creation",
  templateUrl: "./production-creation.component.html",
  styleUrls: ["./production-creation.component.scss"],
  providers: [UnsubscriberService],
})
export class ProductCreationComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  productId: string | null = null;
  currentStep: string = "";
  previousStep: string = "";
  showStepNotAvailable = false;
  product: Product | null = null;
  isEditMode: boolean = false;
  isLoadingProduct: BehaviorSubject<boolean> = new BehaviorSubject(false);
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private notificationService: NotificationAlertService,
    private unsubscriber: UnsubscriberService,
    private spinnerService: NgxSpinnerService,
    private productEditConfirmationService: ProductEditConfirmationService,
  ) {
    super();
  }

  productCreationSteps = productCreationSteps;

  ngOnInit(): void {
    this.isLoadingProduct.subscribe((isL) => {
      if (isL) {
        this.spinnerService.show("loading_overview");
      } else {
        this.spinnerService.hide("loading_overview");
      }
    });
    this.route.paramMap
      .pipe(this.unsubscriber.takeUntilDestroy)
      .subscribe((params) => {
        this.productId = params.get("productId");
        this.isEditMode = !!this.productId;
        this.updateCurrentStep();

        if (this.productId) {
          this.loadProductDetails(this.productId);
        } else {
          this.product = {
            id: "",
            operatorId: "mock-operator-id",
            language: "en",
            title: "",
            status: ProductStatus.DRAFT,
            lastFinishStage: undefined,
            productType: ProductType.TOUR,
            updatedAt: new Date(),
            createdAt: new Date(),
          };
          this.updateSidebarCompletion();
        }
      });
    // update sidebar status completion based on router status
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        this.unsubscriber.takeUntilDestroy,
      )
      .subscribe(() => {
        const oldStep = this.currentStep;
        this.updateCurrentStep();

        // Only reload product details if the step actually changed
        if (this.currentStep !== oldStep) {
          this.previousStep = oldStep;
          this.updateSidebarCompletion();
          if (this.productId) {
            this.loadProductDetails(this.productId);
          }
        }
      });
  }

  private getRelationsForStep(stepId: string): string[] {
    const stepRelationsMap: Record<string, string[]> = {
      "basic-details": ["basic", "tour", "activity"],
      "additional-info": ["infoCustomerNeedToKnow"],
      media: ["media", "images"],
      "themes-categories": ["taxonomy"],
      "meeting-pickup": ["meeting"],
      "tour-activity-details": ["itinerary", "tour"],
      addons: ["features"],
      special: ["basic"],
      "pricing-setup": ["pricing"],
      scheduling: ["scheduling"],
      "confirmation-type": ["bookingSettings"],
      "cancellation-policy": ["cancellationPolicy"],
      "product-requirements": ["requirements"],
      "submit-for-review": ["basic"],
      "product-status": ["basic"],
    };
    return stepRelationsMap[stepId] || [];
  }

  private loadProductDetails(productId: string) {
    this.isLoadingProduct.next(true);
    this.spinnerService.show("product-detail-loading");
    const relations = this.getRelationsForStep(this.currentStep);
    this.productService
      .getProduct({ id: productId }, relations)
      .pipe(this.unsubscriber.takeUntilDestroy)
      .subscribe({
        next: (product) => {
          if (this.product && this.product.id === product.id) {
            this.product = { ...this.product, ...product };
          } else {
            this.product = product;
          }
          this.isLoadingProduct.next(false);
          this.updateSidebarCompletion();
          this.checkEligible(this.product);
        },
        error: (error) => {
          console.log("error ", error);
          this.isLoadingProduct.next(false);
          this.notificationService.showError("Failed to load product details.");
          this.router.navigate(["../.."], { relativeTo: this.route });
        },
      });
  }

  private updateCurrentStep() {
    let childRoute = this.route.snapshot.firstChild;
    // get last segment url
    while (childRoute && childRoute.firstChild) {
      childRoute = childRoute.firstChild;
    }
    if (childRoute && childRoute.routeConfig) {
      const requestedStep = childRoute.routeConfig.path || "";
      this.currentStep = requestedStep;
    }
  }

  checkEligible(product: Product) {
    this.showStepNotAvailable = match(product.lastFinishStage)
      .with(
        P.not(CompletionStageEnum.DONE),
        () =>
          allStepIdsOrdered.indexOf(product.lastFinishStage) + 1 <
          allStepIdsOrdered.indexOf(this.currentStep),
      )
      .otherwise(() => false);
  }

  goToLastAllowedStep(): void {
    const lastAllowedStep = this.product.lastFinishStage;
    const indexOfLastAllowedStep = allStepIdsOrdered.indexOf(lastAllowedStep);
    const nextStep = allStepIdsOrdered[indexOfLastAllowedStep + 1];
    this.router.navigate([nextStep], { relativeTo: this.route });
  }

  /**
   * Updates the completion status of steps in the sidebar based on the product's creationStage.
   */
  private updateSidebarCompletion(): void {
    const getLastFinishedIndex = (
      product: typeof this.product,
      stepIds: string[],
    ): number =>
      product?.lastFinishStage ? stepIds.indexOf(product.lastFinishStage) : -1;

    const isStepCompleted = (
      stepId: string,
      lastFinishedIndex: number,
      stepIds: string[],
    ): boolean => {
      if (lastFinishedIndex === -1) return false;
      const stepIndex = stepIds.indexOf(stepId);
      return stepIndex !== -1 && stepIndex <= lastFinishedIndex;
    };

    const updateStepGroup = (
      group: { steps: Array<{ id: string; completed: boolean }> },
      lastFinishedIndex: number,
      stepIds: string[],
    ) => ({
      ...group,
      steps: group.steps.map((step) => ({
        ...step,
        completed: isStepCompleted(step.id, lastFinishedIndex, stepIds),
      })),
    });

    const lastFinishedIndex = getLastFinishedIndex(
      this.product,
      allStepIdsOrdered,
    );

    this.productCreationSteps = Object.entries(productCreationSteps).reduce(
      (acc, [groupKey, groupValue]) => ({
        ...acc,
        [groupKey]: updateStepGroup(
          groupValue,
          lastFinishedIndex,
          allStepIdsOrdered,
        ),
      }),
      {} as typeof productCreationSteps,
    );

    if (this.product.status === ProductStatus.PUBLISHED) {
      this.productCreationSteps = {
        ...this.productCreationSteps,
        finish: {
          ...this.productCreationSteps.finish,
          steps: this.productCreationSteps.finish.steps.filter(
            (step) => step.id !== "submit-for-review",
          ),
        },
      };
    }
  }

  onStepDataSaved(event: {
    product: Partial<Product>;
    nextRoute: string;
  }): void {
    if (event.product) {
      // Merge partial update with existing product data
      this.product = { ...this.product, ...event.product } as Product;
      this.updateSidebarCompletion();
    }
    if (event.nextRoute) {
      const urlTree = this.router.createUrlTree([`${event.nextRoute}`], {
        relativeTo: this.route,
      });
      this.router.navigateByUrl(urlTree.toString());
    }
  }
  // to pass component data to route outlet node
  onOutletActivate(component: any): void {
    component.product = this.product;
    if (component.dataSaved && component.dataSaved instanceof EventEmitter) {
      component.dataSaved
        .pipe(this.unsubscriber.takeUntilDestroy)
        .subscribe((event: { product: Product; nextRoute: string }) => {
          this.onStepDataSaved(event);
        });
    }
  }
}
